import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Briefcase } from "lucide-react";
import TagInput from "@/components/TagInput";
import { AfyanexLogo } from "@/components/AfyanexLogo";

// Standalone (no Layout / no AuthContext) — must work without a session.
const BASE = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BASE}/api`, withCredentials: false });

export default function PublicApply() {
  const { slug } = useParams();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    source: "Website",
    skills: [],
    languages: [],
    notes: "",
  });

  useEffect(() => {
    let alive = true;
    publicApi
      .get(`/public/agencies/${slug}`)
      .then(({ data }) => { if (alive) setAgency(data); })
      .catch(() => { if (alive) setAgency({ _error: true }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      await publicApi.post(`/public/agencies/${slug}/apply`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        source: form.source.trim() || "Website",
        skills: form.skills,
        languages: form.languages,
        notes: form.notes,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-clinical-bg">
        <Loader2 className="h-5 w-5 animate-spin text-clinical-primary" />
      </div>
    );
  }

  if (!agency || agency._error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-clinical-bg px-6">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-heading">Agency not found</h1>
          <p className="mt-2 text-sm text-clinical-subtext">
            The link you followed isn't valid. Please double-check it with the agency.
          </p>
        </div>
      </div>
    );
  }

  const brand = agency.branding || {};
  const logoUrl = brand.logo_url;

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-clinical-bg px-6" data-testid="public-apply-success">
        <div className="bg-white max-w-md w-full p-8 rounded-2xl border border-clinical-border text-center">
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
          <h1 className="mt-4 text-2xl font-heading">Thanks, {form.name.split(" ")[0]}!</h1>
          <p className="mt-2 text-sm text-clinical-subtext">
            Your application has been sent to <b>{agency.name}</b>. Someone from their team will reach out by email if it's a fit.
          </p>
          <button
            type="button"
            className="mt-6 btn-primary inline-flex items-center"
            onClick={() => { setSubmitted(false); setForm({ ...form, name: "", email: "", phone: "", notes: "" }); }}
            data-testid="public-apply-submit-another"
          >
            Submit another application
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-clinical-bg px-4 py-10" data-testid="public-apply-page">
      <div className="max-w-xl mx-auto">
        <header className="flex items-center gap-3 mb-6">
          {logoUrl ? (
            <img src={logoUrl} alt={agency.name} className="h-10 w-auto" />
          ) : (
            <AfyanexLogo className="h-8 w-auto" />
          )}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-clinical-subtext">Apply to join</div>
            <h1 className="text-xl font-heading font-semibold text-clinical-text">{agency.name}</h1>
          </div>
        </header>

        <form
          onSubmit={submit}
          className="bg-white rounded-2xl border border-clinical-border p-6 space-y-4"
        >
          <p className="text-sm text-clinical-subtext flex items-start gap-2">
            <Briefcase className="h-4 w-4 mt-0.5 shrink-0 text-clinical-primary" />
            Tell us a bit about yourself. Required fields are marked.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full name *">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-clinical"
                required
                data-testid="apply-name"
              />
            </Field>
            <Field label="Email *">
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-clinical"
                required
                data-testid="apply-email"
              />
            </Field>
            <Field label="Phone">
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="input-clinical"
                data-testid="apply-phone"
              />
            </Field>
            <Field label="How did you hear about us?">
              <input
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                className="input-clinical"
                placeholder="Indeed, Referral, Facebook…"
              />
            </Field>
          </div>

          <Field label="Skills">
            <TagInput
              value={form.skills}
              onChange={(v) => setForm({ ...form, skills: v })}
              suggestions={["dementia-care", "hoyer-lift", "cpr", "first-aid", "wound-care", "med-pass"]}
              placeholder="Add a skill and press Enter"
            />
          </Field>

          <Field label="Languages spoken">
            <TagInput
              value={form.languages}
              onChange={(v) => setForm({ ...form, languages: v })}
              suggestions={["en", "es", "fr", "ar", "pt", "tl", "zh"]}
              placeholder="en, es…"
            />
          </Field>

          <Field label="Tell us about your experience">
            <textarea
              rows={4}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input-clinical"
              placeholder="Years of experience, certifications, availability, etc."
              data-testid="apply-notes"
            />
          </Field>

          <button
            type="submit"
            disabled={saving}
            className="btn-primary w-full justify-center"
            data-testid="apply-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}
          </button>
          <p className="text-[11px] text-clinical-subtext text-center">
            By applying, you agree to {agency.name} contacting you about this position.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-eyebrow mb-1 block">{label}</label>
      {children}
    </div>
  );
}
